import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useSelector } from 'react-redux';
import { useFetchye } from 'fetchye';
import { ProgressCircular } from '@americanexpress/dls-react';
import { IconWarning } from '@americanexpress/dls-icons';
import { onCLS, onLCP, onINP } from 'web-vitals';
import Element from '../Element';
import { ChildContext, MemberContext, BalanceContext } from '../context';
import {
  parseHtml,
  getPlainUuid,
  getUniqueCode,
  getCurrentDate,
  maskString,
  formatDate,
  getCurrentPage,
} from '../../utils/util';
import {
  filterMemberDetails,
  mandatoryUrlParamsCheck,
  validateUrlParams,
  getErrorStrings,
  getRequiredData,
  getCuidStatus,
  memberApiCall,
  getParamsFromSessionStorage,
  validateUrlParamsForValidUrlValue,
} from '../../Methods/Index';
import { getMemberEmail, handleDecryptApi } from '../../utils/utilService';
import { updateWebVitals, parameters } from './uiWebVitals';
// import formData from '../../../mock/files/formJsons/franceCorporate_bta_common_v2.json';

/**
 * entry point of the form generator utility
 * @param
 */
/* eslint complexity: ["error", 30] -- need to increase complexity */

const Home = (props) => {
  const { formDocument } = props;

  const [formContent, setFormContent] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [balanceDetails, setBalanceDetails] = useState(null);
  const [upiId, setUpiId] = useState(null);
  const contextVal = useContext(ChildContext);
  const [containerBg, setContainerBg] = useState('container fluid pad-1-tb dls-gray-02-bg');
  const submitApiUrls = useSelector((state) => state.getIn(['config', 'submitApiUrls']));
  const oneCmsUrls = useSelector((state) => state.getIn(['config', 'oneCmsUrls']));
  const memberApiUrls = useSelector((state) => state.getIn(['config', 'memberApiUrls']));
  const jId = window.sessionStorage.getItem('jId');
  const path = window.location.pathname;
  const urlPageType = path.split('/')[2];
  const [atWorkUserData, setAtWorkUserData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const { data } = useFetchye(
    `${oneCmsUrls}/Internet/eforms/data/iformsPlus/${contextVal.cuid}.json`
  );
  const [oneCmsData, setOneCmsData] = useState(null);

  const isFormDocumentValid = formDocument !== undefined
    && formDocument
    && Object.keys(formDocument).length > 0;

  useEffect(() => {
    if (!isFormDocumentValid && data && data.body) {
      setOneCmsData({ ...data });
    }
  }, [data]);

  useEffect(() => {
    if (isFormDocumentValid) {
      setFormContent({ ...formDocument[0] });
      const tempData = {};
      tempData.body = { ...formDocument };
      setOneCmsData({ ...tempData });
    }
    onCLS(updateWebVitals, { reportAllChanges: true });
    onLCP(updateWebVitals, { reportAllChanges: true });
    onINP(updateWebVitals, { reportAllChanges: true });
  }, []);

  const extractQueryParams = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const urlParams = Object.fromEntries(urlSearchParams.entries());
    return urlParams;
  };

  /* eslint-disable max-len -- need */
  const [globalParamsState, dispatchGlobalParamsState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { globalParams: extractQueryParams(), isUrlValid: false }
  );
  /**
   * splunk logging
   */
  // eslint-disable-next-line max-params -- logger helper needs 4 contextual arguments
  const loggerApiCall = async (log, logType, additionalPageInfo = {}, page = '') => {
    const currentPage = page === '' ? getCurrentPage() : page;
    const loggerPayload = {
      journeyId: jId,
      cuid: contextVal.cuid,
      message: `oneformsUI ${log} timestamp=${new Date().toISOString()}`,
      type: logType,
      page: currentPage,
      // additionalAttribute:null,
    };
    if (log.includes('page=thankyou') || log.includes('page=error')) {
      loggerPayload.additionalAttribute = {
        lcp: parameters.lcp,
        cls: parameters.cls,
        inp: parameters.inp,
      };
    }

    if (additionalPageInfo && Object.keys(additionalPageInfo).length > 0) {
      loggerPayload.additionalAttribute = {
        ...loggerPayload.additionalAttribute,
        ...additionalPageInfo,
      };
    }

    const loggerRequestOption = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loggerPayload),
    };
    // console.log(loggerPayload.message);
    const response = await fetch(`${submitApiUrls}/iForms/logger?cuid=${contextVal.cuid}&page=${currentPage}`, loggerRequestOption);
    const result = await response.json();
    return result;
  };

  // Fetch member details --> Member Service Api Call
  const fetchMemberDetails = async (container, errorStrings, params) => {
    const memberDataObject = {
      eligibleCards: [],
      otherMemberData: {},
    };
    const { memberFilter, memberCallQueryParams, countriesSupported } = container;
    const memberData = await memberApiCall({
      memberCallQueryParams,
      params,
      memberApiUrls,
      jId,
      cuid: contextVal.cuid,
    });
    if (memberData && memberData.statusIndicator === 'T') {
      const logString1 = 'status=memberApiCallSuccess';
      loggerApiCall(logString1, 'info', {}, 'onLoad');

      const lMemberDetails = memberData.memberDetails;
      // check for countries supported by this form
      // Add this case under errorMessage
      if (countriesSupported) {
        let match = false;
        const userLocale = lMemberDetails[0].holder.localizationPreferences.geoCountryLocale.split(
          '-'
        )[1];
        countriesSupported.forEach((allowedCountry) => {
          if (userLocale === allowedCountry) {
            match = true;
          }
        });
        if (!match) {
          setErrorMessage(errorStrings.userNotAllowed);
          const logString = 'status=userIsNotAllowedToAccessThisForm';
          loggerApiCall(logString, 'info', {}, 'onLoad');
        }
      }
      /**
       * Filter memberData cards based on filter criteria if any.
       * After filter set the member details accordingly
       */
      if (memberFilter) {
        const filteredOptions = filterMemberDetails({
          memberFilter,
          lMemberDetails,
          params,
          loggerApiCall,
          errorStrings,
        });
        if (filteredOptions.length > 0) {
          const logString = 'status=settingMemberDetails msg=filterAppliedSuccessfully';
          loggerApiCall(logString, 'info', {}, 'onLoad');
          memberDataObject.eligibleCards = filteredOptions;
        } else {
          // log No Valid cards found for this filter criteria
          const logString = 'status=noValidCardsFound';
          loggerApiCall(logString, 'info', {}, 'onLoad');
          setErrorMessage(errorStrings.noValidCardsFound);
        }
      } else {
        // No memberFilter in UI case
        if (memberData.memberDetails.length === 0) {
          const logString = 'status=noValidCardsFound';
          loggerApiCall(logString, 'info', {}, 'onLoad');
          setErrorMessage(errorStrings.noValidCardsFound);
        } else {
          const logString = 'status=settingMemberDetails msg=noCardFilterOnUISide';
          loggerApiCall(logString, 'info', {}, 'onLoad');
        }
        memberDataObject.eligibleCards = memberData.memberDetails;
      }
      // fetch member email --> always for first card
      if (
        container.callMemberEmailApiOnLoad
        && memberDataObject.eligibleCards.length > 0
      ) {
        const { accountToken } = memberDataObject.eligibleCards[0];
        const result = await getMemberEmail({
          dataObject: {
            iformIntegrationUrls: memberApiUrls,
            memberInfo: { accountToken },
            journeyId: jId,
            cuid: contextVal.cuid,
          },
          functionObject: { loggerApiCall },
        });
        if (result.statusIndicator === 'T') {
          memberDataObject.otherMemberData.cmEmail = result.data.digResponse.email;
        } else {
          setErrorMessage(errorStrings.failedMemberEmail);
        }
      }
    } else {
      // We load progression circle in this case
      // Need to display failed member call message pr try again?
      if (memberData && memberData.statusCode === 498) {
        setErrorMessage(errorStrings.invalidCredentials);
      } else {
        setErrorMessage(errorStrings.failedMemberApiCall);
      }
      const logString = `status=memberApiCallFailed errorMsg=${memberData.message ? memberData.message : 'memberCallFailed'}`;
      loggerApiCall(logString, 'error', {}, 'onLoad');
    }
    return memberDataObject;
  };

  const onloadfunction = async (container) => {
    let params = { ...globalParamsState.globalParams };
    let isUrlValid = true;
    const errorStrings = getErrorStrings(container[0].formContainer, params);
    /**
     * Checks if url has all the required params
     */
    if (
      container[0].formContainer.mandatoryUrlParams
      && container[0].formContainer.mandatoryUrlParams.length > 0
    ) {
      if (
        !mandatoryUrlParamsCheck(
          container[0].formContainer.mandatoryUrlParams,
          params
        )
      ) {
        // check if window session storage has params for this cuid
        params = getParamsFromSessionStorage(
          contextVal.cuid,
          container[0].formContainer.parentCuid
        );
        if (params) {
          // should we set only those mandatory params which are missing in the url?
          // append these params to the url
          const url = new URL(window.location.href);
          const searchParams = new URLSearchParams(url.search);
          Object.keys(params).forEach((key) => {
            searchParams.set(key, params[key]);
          });
          const logString = `status=invalidUrlFound oldUrl= ${window.location.href} newUrl= ${url.pathname}?${searchParams}`;
          loggerApiCall(logString, 'info', {}, 'onLoad');
          window.history.replaceState({}, '', `${url.pathname}?${searchParams}`);
          isUrlValid = true;
        } else {
          isUrlValid = false;
        }
      } else {
        // get the prev params from broswer session storage and override the current params for this cuid
        // should we put only the mandatory params in session storage?
        isUrlValid = true;
        const iformSessionParamsObject = window.sessionStorage.getItem(
          'iformSessionParams'
        )
          ? JSON.parse(window.sessionStorage.getItem('iformSessionParams'))
          : {};
        delete params.page; // delete page key from params
        iformSessionParamsObject[contextVal.cuid] = params;
        window.sessionStorage.setItem(
          'iformSessionParams',
          JSON.stringify(iformSessionParamsObject)
        );
      }
    }
    /**
     * Checks if a url parameter has a value as url string
     * If yes then this url host should be a amex host or allowed via json
     */
    if (isUrlValid) {
      const allowedHosts = container[0].formContainer.allowedUrlParamHosts
        ? container[0].formContainer.allowedUrlParamHosts
        : ['www.americanexpress.com'];
      isUrlValid = validateUrlParamsForValidUrlValue(params, allowedHosts);
      if (!isUrlValid) {
        const logString = 'status=forbidden value of url found in parameters';
        loggerApiCall(logString, 'info', {}, 'onLoad');
      }
    }

    if (isUrlValid) {
      const { urlParamsValidations } = container[0].formContainer;
      isUrlValid = validateUrlParams(params, urlParamsValidations);
      if (!isUrlValid) {
        const logString = `status=invalidUrlParameters oldUrl= ${window.location.href}`;
        loggerApiCall(logString, 'info', {}, 'onLoad');
      }
    }

    if (isUrlValid) {
      const cuidStatusActive = await getCuidStatus({
        memberApiUrls,
        pageType: container[0].formContainer.pageType,
        urlPageType,
        jId,
        cuid: contextVal.cuid,
        loggerApiCall,
        errorStrings,
        setErrorMessage,
      });
      if (cuidStatusActive) {
        if (container[0].formContainer.cssProperties.containerBg) {
          setContainerBg(container[0].formContainer.cssProperties.containerBg);
        }
        if (container[0].formContainer.decryptParams) {
          // const urlParams = new URLSearchParams(window.location.search);
          // params has all the params from url
          const { decryptParams } = container[0].formContainer;

          const result = await handleDecryptApi({
            decryptParams,
            params,
            memberApiUrls,
            jId,
            cuid: contextVal.cuid,
          });
          if (result.statusIndicator === 'T') {
            loggerApiCall('status=decryptApiCallSuccess', 'info', {}, 'onLoad');
            params = { ...params, ...JSON.parse(result?.data) };
          } else {
            loggerApiCall(`status=decryptApiCallFailed message:${result.message}`, 'error', {}, 'onLoad');
            setErrorMessage(errorStrings.failedDecryptApiCall);
            isUrlValid = false;
          }
        }
        if (container[0].formContainer.cssProperties.containerBg) {
          setContainerBg(container[0].formContainer.cssProperties.containerBg);
        }
        // add checker methods to add someids / run create functions
        if (container[0].formContainer.paramsCheck) {
          container[0].formContainer.paramsCheck.forEach((paramObj) => {
            if (
              Object.prototype.hasOwnProperty.call(params, paramObj.idToCheck)
            ) {
              // idToCheck is present
              if (
                paramObj.actions.idToCheckPresent
                && paramObj.actions.idToCheckPresent.length > 0
              ) {
                paramObj.actions.idToCheckPresent.forEach((actionObj) => {
                  switch (actionObj.action) {
                    case 'addKeyValues':
                      Object.keys(actionObj.keyValuesToAdd).forEach((key) => {
                        params[key] = actionObj.keyValuesToAdd[key];
                      });
                      break;
                    default:
                      params = null;
                  }
                });
              }
            } else if (
              paramObj.actions.idToCheckMissing
              && paramObj.actions.idToCheckMissing.length > 0
            ) {
              paramObj.actions.idToCheckMissing.forEach((actionObj) => {
                switch (actionObj.action) {
                  case 'callGetPlainUuid':
                    params[actionObj.addAs] = actionObj.addAs === 'docId' ? jId : getPlainUuid();
                    break;
                  case 'callGetUniqueCode':
                    {
                      const tempVal = getUniqueCode();
                      params[actionObj.addAs] = tempVal;
                    }
                    break;
                  case 'addKeyValues':
                    Object.keys(actionObj.keyValuesToAdd).forEach((key) => {
                      params[key] = actionObj.keyValuesToAdd[key];
                    });
                    break;
                  case 'callGetTodayDate':
                    {
                      const tempVal = getCurrentDate();
                      /* eslint-disable-next-line prefer-destructuring -- desc */
                      params[actionObj.addAs] = tempVal.split('T')[0];
                    }
                    break;
                  default:
                    params = null;
                }
              });
            }
          });
        }
        if (isUrlValid && container[0].formContainer.pageType === 'secure') {
          if (urlPageType === 'at-secure') {
            getRequiredData({
              container,
              errorStrings,
              jId,
              memberApiUrls,
              setAtWorkUserData,
              setErrorMessage,
              cuid: contextVal.cuid,
              loggerApiCall,
            });
          } else if (container[0]?.formContainer?.bypassMemberApi) {
            const logString = 'status=bypassMemberApi';
            loggerApiCall(logString, 'info', {}, 'onLoad');
            setMemberDetails([]);
          } else {
            const logString = 'status=calledMemberApi';
            loggerApiCall(logString, 'info', {}, 'onLoad');
            const memberDataObj = await fetchMemberDetails(container[0].formContainer, errorStrings, params);
            setMemberDetails(memberDataObj.eligibleCards);
            params = { ...params, ...memberDataObj.otherMemberData };
          }
        }
        if (container[0].formContainer.maskParams) {
          container[0].formContainer.maskParams.forEach((maskObj) => {
            if (Object.prototype.hasOwnProperty.call(params, maskObj.param)) {
              params[maskObj.addAs] = maskString({
                ...maskObj,
                stringToMask: params[maskObj.param],
              });
            }
          });
        }
        if (container[0].formContainer.formatDate) {
          const actionObj = container[0].formContainer.formatDate;
          Object.keys(actionObj).forEach((index) => {
            const formatObj = actionObj[index];
            if (formatObj.reqCurrentDate && formatObj.reqCurrentDate === 'Y') {
              params[formatObj.keyToAdd] = formatDate(
                new Date(),
                formatObj.format,
                formatObj.locale
              );
            } else if (formatObj.keyToFormat) {
              const date = new Date(params[formatObj.keyToFormat]);
              params[formatObj.keyToAdd] = formatDate(
                date,
                formatObj.format,
                formatObj.locale
              );
            }
          });
        }
        setFormContent(container[0]);
        dispatchGlobalParamsState({ globalParams: { ...params }, isUrlValid }); // update the global param state and then only proceed for form rendering
      }
    } else {
      const logString = `status=invalidUrl - ${window.location.href} errorMsg=invalid url and no parameters found in session storage`;
      loggerApiCall(logString, 'error', {}, 'onLoad');
      setErrorMessage(errorStrings.invalidUrl);
    }
  };
  /* eslint-enable max-len -- need */
  useEffect(() => {
    if (oneCmsData && oneCmsData.body) {
      onloadfunction(oneCmsData.body);
    }
  }, [oneCmsData]);
  /*
   * Use the below code for local run */
  // useEffect(() => {
  //   if (formData) {
  //     onloadfunction(formData);
  //   }
  // }, [formData]);

  const BalanceContextProviderValue = useMemo(
    () => ({
      balanceDetails,
      setBalanceDetails,
      upiId,
      setUpiId,
    }),
    [balanceDetails, setBalanceDetails, upiId, setUpiId]
  );

  // handle no form content and no member details properly
  // merge errorMessage and userNot allowed properly.
  return (
    <div className={containerBg}>
      {formContent
        && globalParamsState.isUrlValid
        && formContent.formContainer.pageType === 'secure'
        && urlPageType === 'secure'
        && memberDetails
        && memberDetails.length > 0
        && !errorMessage && (
          <MemberContext.Provider value={memberDetails}>
            <BalanceContext.Provider value={BalanceContextProviderValue}>
              <Element
                formContent={formContent}
                loggerApiCall={loggerApiCall}
                journeyId={jId}
                urlParams={globalParamsState.globalParams}
              />
            </BalanceContext.Provider>
          </MemberContext.Provider>
      )}
      {formContent
        && globalParamsState.isUrlValid
        && (formContent.formContainer.pageType === 'unsecure'
          || (formContent.formContainer.pageType === 'secure'
            && ((urlPageType === 'at-secure' && atWorkUserData)
              || formContent?.formContainer?.bypassMemberApi)))
        && !errorMessage && (
          <Element
            formContent={formContent}
            loggerApiCall={loggerApiCall}
            journeyId={jId}
            urlParams={{ ...globalParamsState.globalParams, ...atWorkUserData }}
          />
      )}
      {errorMessage && (
        <div>
          <div className="margin-3-t text-align-center col-lg-12">
            <IconWarning
              isFilled={true}
              title="Error"
              titleId="error"
              size="xl"
              color="caution"
            />
            <p className="heading-3 margin-2-t text-align-center col-md-12">
              {parseHtml(errorMessage)}
            </p>
          </div>
        </div>
      )}
      {!errorMessage
        && (!formContent
          || (formContent
            && formContent.formContainer.pageType === 'secure'
            && !memberDetails
            && !atWorkUserData)) && (
            <section id="spinner">
              <div className="pad flex flex-justify-center flex-align-items-center">
                <ProgressCircular size="lg" />
              </div>
            </section>
      )}
    </div>
  );
};

Home.propTypes = {
  formDocument: PropTypes.objectOf(PropTypes.shape),
};

export default Home;
